/**
 * AdminWorkshopsTab - Admin management of practitioner workshops
 * Handles workshop approval, rejection, viewing full content, and deletion
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CheckCircle, XCircle, Clock, Eye, DollarSign, Users, 
  BookOpen, ChevronDown, ChevronUp, Loader2, Search,
  Tag, Trash2, Folder, FileText, Video, Music, Link as LinkIcon,
  ExternalLink, AlertTriangle, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { PlatformFeeDefaultCard, ItemPlatformFeeEditor } from '../PlatformFeeControl';
import { API } from '@/App';
import { toast } from 'sonner';
import { Pagination } from '@/components/Pagination';

// Workshop Status Badge
const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { label: 'Draft', className: 'bg-gray-500' },
    pending_review: { label: 'Pending Review', className: 'bg-yellow-500' },
    pending_admin_approval: { label: 'Pending Approval', className: 'bg-yellow-500' },
    approved: { label: 'Approved', className: 'bg-green-500' },
    live: { label: 'Live', className: 'bg-green-600' },
    rejected: { label: 'Rejected', className: 'bg-red-500' },
    published: { label: 'Published', className: 'bg-cyan-500' },
    inactive: { label: 'Inactive', className: 'bg-orange-500' }
  };
  const config = statusConfig[status] || statusConfig.draft;
  return <Badge className={`${config.className} text-white`}>{config.label}</Badge>;
};

// Modified Since Approval Badge
const ModifiedBadge = () => (
  <Badge className="bg-amber-500 text-white animate-pulse">
    Modified
  </Badge>
);

// Content Type Icon
const ContentTypeIcon = ({ type }) => {
  const icons = {
    video: <Video className="w-4 h-4 text-blue-400" />,
    meditation: <Music className="w-4 h-4 text-purple-400" />,
    blog: <FileText className="w-4 h-4 text-green-400" />,
    exercise: <FileText className="w-4 h-4 text-orange-400" />,
    link: <LinkIcon className="w-4 h-4 text-cyan-400" />,
    audio: <Music className="w-4 h-4 text-pink-400" />
  };
  return icons[type] || <FileText className="w-4 h-4 text-gray-400" />;
};

// Content Item - ALWAYS shows content, no expand/collapse needed
const ContentItemDisplay = ({ content }) => {
  const isVideo = content.content_type === 'video' || content.file_url?.includes('youtube') || content.file_url?.includes('vimeo');
  const isAudio = content.content_type === 'audio' || content.content_type === 'meditation';
  const isLink = content.content_type === 'link';
  
  // Extract YouTube embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };
  
  return (
    <div className="bg-slate-700 rounded overflow-hidden border border-slate-500 mb-3">
      {/* Content Header */}
      <div className="flex items-center gap-2 p-2 text-sm bg-slate-600">
        <ContentTypeIcon type={content.content_type} />
        <span className="flex-1 text-white font-medium">{content.title}</span>
        <Badge variant="outline" className="text-xs text-white border-gray-500">{content.content_type}</Badge>
        {content.file_url && (
          <a 
            href={content.file_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-1 bg-cyan-600 hover:bg-cyan-500 rounded text-white text-xs px-2"
          >
            Open in New Tab ↗
          </a>
        )}
      </div>
      
      {/* Content Preview - ALWAYS VISIBLE */}
      <div className="p-3 bg-slate-800">
        {content.description && (
          <p className="text-sm text-gray-300 mb-3">{content.description}</p>
        )}
        
        {/* Video Player */}
        {isVideo && content.file_url && (
          <div className="aspect-video w-full rounded overflow-hidden bg-black">
            <iframe
              src={getEmbedUrl(content.file_url)}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={content.title}
            />
          </div>
        )}
        
        {/* Audio Player */}
        {isAudio && content.file_url && (
          <audio controls className="w-full">
            <source src={content.file_url} />
            Your browser does not support the audio element.
          </audio>
        )}
        
        {/* Link Display */}
        {isLink && content.file_url && (
          <a 
            href={content.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 underline text-lg"
          >
            <ExternalLink className="w-5 h-5" />
            {content.file_url}
          </a>
        )}
        
        {/* Text/Blog Content */}
        {(content.content_type === 'blog' || content.content_type === 'exercise') && content.content_text && (
          <div className="prose prose-invert max-w-none text-sm bg-slate-700 p-3 rounded">
            <div dangerouslySetInnerHTML={{ __html: content.content_text }} />
          </div>
        )}
        
        {/* File URL for other types */}
        {!isVideo && !isAudio && !isLink && content.file_url && (
          <div className="flex items-center gap-2 bg-slate-700 p-2 rounded">
            <span className="text-gray-400 text-sm">File URL:</span>
            <a 
              href={content.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 underline text-sm"
            >
              {content.file_url}
            </a>
          </div>
        )}
        
        {/* No content message */}
        {!content.file_url && !content.content_text && (
          <p className="text-gray-400 italic text-sm">No content URL or text provided</p>
        )}
      </div>
    </div>
  );
};

// Full Workshop Detail Modal - Shows ALL content by default, no expand/collapse
const WorkshopDetailModal = ({ workshop, open, onClose, onDelete, onAcknowledge, loading, defaultFeePct, onFeeSaved }) => {
  if (!workshop) return null;

  const feePct = workshop.platform_fee_percent ?? defaultFeePct ?? 20;
  const formatPrice = (price) => `$${(price || 0).toFixed(2)}`;
  const isActive = ['approved', 'live', 'published'].includes(workshop.status);
  const isModified = workshop.modified_since_approval && isActive;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden bg-slate-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-white">
            {workshop.title}
            <StatusBadge status={workshop.status} />
            {isModified && <ModifiedBadge />}
          </DialogTitle>
          <p className="text-sm text-gray-300">
            by {workshop.creator_name} ({workshop.creator?.email})
          </p>
        </DialogHeader>
        
        {/* Modified Workshop Alert */}
        {isModified && (
          <div className="bg-amber-500/10 border border-amber-500 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="font-medium text-amber-400">Workshop Modified Since Approval</p>
                  <p className="text-sm text-gray-300">
                    Content has been changed since this workshop was last approved.
                    {workshop.last_modified_at && (
                      <span> Last modified: {new Date(workshop.last_modified_at).toLocaleDateString()}</span>
                    )}
                  </p>
                </div>
              </div>
              <Button 
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => onAcknowledge && onAcknowledge(workshop)}
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Acknowledge
              </Button>
            </div>
          </div>
        )}
        
        {/* Scrollable Content Area - Using native scroll */}
        <div className="overflow-y-auto max-h-[70vh] pr-2" style={{ scrollbarWidth: 'thin' }}>
          <div className="space-y-6">
            {/* Cover Image */}
            {workshop.cover_image_url && (
              <img 
                src={workshop.cover_image_url} 
                alt={workshop.title}
                className="w-full h-48 object-cover rounded-lg"
              />
            )}
            
            {/* Description */}
            <div>
              <h3 className="font-semibold mb-2 text-white">Description</h3>
              <p className="text-gray-200">{workshop.description}</p>
            </div>
            
            {/* Pricing */}
            <div className="bg-slate-800 p-4 rounded-lg">
              <h3 className="font-semibold mb-3 text-white">Pricing Breakdown</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-gray-200">Base Price:</span>
                <span className="text-white">{formatPrice(workshop.price)}</span>
                <span className="text-gray-200">Platform Fee ({feePct}%):</span>
                <span className="text-white">-{formatPrice(workshop.platform_fee ?? workshop.price * (feePct / 100))}</span>
                <span className="text-gray-200 font-medium">Creator Receives:</span>
                <span className="text-white font-medium">{formatPrice(workshop.practitioner_net ?? workshop.price * (1 - feePct / 100))}</span>
                <span className="text-gray-200">Processing, buyer pays (3% + $0.30):</span>
                <span className="text-white">{formatPrice(workshop.processing_fee ?? (workshop.price * 0.03 + 0.30))}</span>
                <span className="text-gray-200 font-medium">User Pays:</span>
                <span className="text-cyan-400 font-medium">{formatPrice(workshop.total_price ?? (workshop.price * 1.03 + 0.30))}</span>
              </div>
              <ItemPlatformFeeEditor kind="workshop" item={workshop} onSaved={onFeeSaved} />
            </div>
            
            {/* Tags */}
            {workshop.tags?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 text-white">Tags</h3>
                <div className="flex flex-wrap gap-1">
                  {workshop.tags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-white border-gray-500">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Collaborators */}
            {workshop.collaborators?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 text-white">Collaborators</h3>
                <div className="space-y-2">
                  {workshop.collaborators.map(collab => (
                    <div key={collab.id} className="flex items-center justify-between bg-slate-800 p-2 rounded">
                      <span className="text-white">{collab.user_name} ({collab.user?.email})</span>
                      <Badge variant="outline" className="text-white border-gray-500">{collab.income_percentage}% share</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Content Structure - ALWAYS SHOWS ALL CONTENT */}
            <div>
              <h3 className="font-semibold mb-3 text-white text-lg border-b border-slate-600 pb-2">
                Workshop Content ({workshop.modules?.length || 0} modules)
              </h3>
              
              {workshop.modules?.length === 0 ? (
                <p className="text-gray-300 italic">No modules added yet</p>
              ) : (
                <div className="space-y-6">
                  {workshop.modules?.map((module, mi) => (
                    <div key={module.id} className="border-2 border-cyan-600 rounded-lg overflow-hidden">
                      {/* Module Header */}
                      <div className="bg-cyan-900 p-3">
                        <h4 className="font-bold flex items-center gap-2 text-white text-lg">
                          <BookOpen className="w-5 h-5 text-cyan-300" />
                          Module {mi + 1}: {module.title}
                        </h4>
                        {module.description && (
                          <p className="text-sm text-cyan-100 mt-1">{module.description}</p>
                        )}
                        <p className="text-xs text-cyan-300 mt-2">
                          {module.folders?.length || 0} folders • {module.direct_content?.length || 0} direct content items
                        </p>
                      </div>
                      
                      {/* Module Content - ALWAYS VISIBLE */}
                      <div className="p-4 space-y-4 bg-slate-800">
                        {/* Direct Content */}
                        {module.direct_content?.length > 0 && (
                          <div>
                            <h5 className="text-sm text-yellow-400 uppercase font-bold mb-3 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Direct Content ({module.direct_content.length})
                            </h5>
                            {module.direct_content.map(content => (
                              <ContentItemDisplay key={content.id} content={content} />
                            ))}
                          </div>
                        )}
                        
                        {/* Folders */}
                        {module.folders?.length > 0 && (
                          <div>
                            <h5 className="text-sm text-yellow-400 uppercase font-bold mb-3 flex items-center gap-2">
                              <Folder className="w-4 h-4" />
                              Folders ({module.folders.length})
                            </h5>
                            {module.folders.map(folder => (
                              <div key={folder.id} className="border-2 border-yellow-600 rounded-lg mb-4 overflow-hidden">
                                {/* Folder Header */}
                                <div className="bg-yellow-900 p-2 flex items-center gap-2">
                                  <Folder className="w-5 h-5 text-yellow-300" />
                                  <span className="font-bold text-white">{folder.title}</span>
                                  <Badge variant="outline" className="text-xs text-white border-yellow-400">
                                    {folder.content?.length || 0} items
                                  </Badge>
                                </div>
                                
                                {/* Folder Content - ALWAYS VISIBLE */}
                                <div className="p-3 bg-slate-700">
                                  {folder.content?.length > 0 ? (
                                    <div>
                                      {folder.content.map(content => (
                                        <ContentItemDisplay key={content.id} content={content} />
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-gray-400 italic text-sm">No content in this folder</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Empty module */}
                        {(!module.folders || module.folders.length === 0) && (!module.direct_content || module.direct_content.length === 0) && (
                          <p className="text-gray-300 italic">No content in this module</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex justify-between mt-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                <Trash2 className="w-4 h-4 mr-1" /> Delete Workshop
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Workshop?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete &quot;{workshop.title}&quot; and all its modules, folders, and content.
                  This action cannot be undone.
                  {workshop.sales_count > 0 && (
                    <span className="block mt-2 text-red-400">
                      Warning: This workshop has {workshop.sales_count} sales.
                    </span>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => onDelete(workshop)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete Permanently
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Workshop Card
const WorkshopCard = ({ workshop, onView, onApprove, onReject, onDelete, onDeactivate, onReactivate, onAcknowledgeModification }) => {
  const [expanded, setExpanded] = useState(false);
  const isPending = workshop.status === 'pending_review' || workshop.status === 'pending_admin_approval';
  const isActive = workshop.status === 'approved' || workshop.status === 'live' || workshop.status === 'published';
  const isInactive = workshop.status === 'inactive';
  const isModified = workshop.modified_since_approval && isActive;
  
  // Count total content items
  const totalContent = workshop.modules?.reduce((acc, m) => {
    const folderContent = m.folders?.reduce((fAcc, f) => fAcc + (f.content?.length || 0), 0) || 0;
    const directContent = m.direct_content?.length || 0;
    return acc + folderContent + directContent;
  }, 0) || 0;
  
  return (
    <Card className="mb-4 bg-slate-800">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              {workshop.title}
              <StatusBadge status={workshop.status} />
              {isModified && <ModifiedBadge />}
            </CardTitle>
            <CardDescription className="text-gray-300">
              by {workshop.creator_name} • ${workshop.price?.toFixed(2)}
            </CardDescription>
          </div>
          {workshop.cover_image_url && (
            <img 
              src={workshop.cover_image_url} 
              alt="" 
              className="w-16 h-16 object-cover rounded ml-4"
            />
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <p className="text-sm text-gray-300 mb-4 line-clamp-2">{workshop.description}</p>
        
        {/* Tags */}
        {workshop.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {workshop.tags.map((tag, i) => (
              <Badge key={i} variant="outline" className="text-xs text-white border-gray-500">{tag}</Badge>
            ))}
          </div>
        )}
        
        {/* Stats */}
        <div className="flex flex-wrap gap-4 text-sm text-gray-300 mb-4">
          <span><BookOpen className="w-4 h-4 inline mr-1" />{workshop.modules?.length || 0} modules</span>
          <span><FileText className="w-4 h-4 inline mr-1" />{totalContent} content items</span>
          <span><Users className="w-4 h-4 inline mr-1" />{workshop.collaborators?.length || 0} collaborators</span>
          {workshop.sales_count > 0 && (
            <span><DollarSign className="w-4 h-4 inline mr-1" />{workshop.sales_count} sales</span>
          )}
        </div>
        
        {/* Quick expand for module list */}
        {workshop.modules?.length > 0 && (
          <>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setExpanded(!expanded)}
              className="w-full mb-3"
            >
              {expanded ? 'Hide Modules' : 'Show Modules'}
              {expanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </Button>
            
            {expanded && (
              <div className="bg-slate-700 p-3 rounded mb-4 text-sm">
                {workshop.modules.map((m, i) => (
                  <div key={m.id} className="flex items-center justify-between py-1 text-white">
                    <span>{i + 1}. {m.title}</span>
                    <span className="text-gray-400">
                      {m.folders?.length || 0} folders
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onView(workshop)}
            data-testid={`admin-workshop-view-${workshop.id}`}
          >
            <Eye className="w-4 h-4 mr-1" /> View Full Details
          </Button>
          
          {isPending && (
            <>
              <Button 
                size="sm" 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => onApprove(workshop)}
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                onClick={() => onReject(workshop)}
              >
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
            </>
          )}
          
          {/* Acknowledge Modifications button for modified workshops */}
          {isModified && (
            <Button 
              size="sm" 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => onAcknowledgeModification(workshop)}
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Acknowledge Changes
            </Button>
          )}
          
          {/* Deactivate button for active workshops */}
          {isActive && (
            <Button 
              size="sm" 
              variant="outline"
              className="text-orange-400 border-orange-400 hover:bg-orange-400/10"
              onClick={() => onDeactivate(workshop)}
            >
              <XCircle className="w-4 h-4 mr-1" /> Deactivate
            </Button>
          )}
          
          {/* Reactivate button for inactive workshops */}
          {isInactive && (
            <Button 
              size="sm" 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onReactivate(workshop)}
            >
              <CheckCircle className="w-4 h-4 mr-1" /> Reactivate
            </Button>
          )}
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Workshop?</AlertDialogTitle>
                <AlertDialogDescription>
                  Delete &quot;{workshop.title}&quot; and all its content? This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => onDelete(workshop)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};

// Rejection Modal
const RejectModal = ({ open, onClose, onConfirm, workshopTitle }) => {
  const [reason, setReason] = useState('');
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Workshop</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Rejecting &quot;{workshopTitle}&quot;. Please provide a reason:
          </p>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejection..."
            rows={4}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            variant="destructive" 
            onClick={() => { onConfirm(reason); setReason(''); }}
            disabled={!reason.trim()}
          >
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const AdminWorkshopsTab = ({ onPendingCountChange }) => {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [workshopToReject, setWorkshopToReject] = useState(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [feeVersion, setFeeVersion] = useState(0);
  const [defaultFeePct, setDefaultFeePct] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  const getToken = () => localStorage.getItem('token');
  
  // Fetch workshops
  useEffect(() => {
    const fetchWorkshops = async () => {
      const token = getToken();
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/workshops/admin/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWorkshops(response.data);
        setError(null);
        
        // Count pending workshops AND modified workshops as items needing attention
        const pendingCount = response.data.filter(
          w => w.status === 'pending_review' || w.status === 'pending_admin_approval'
        ).length;
        const modifiedCount = response.data.filter(
          w => w.modified_since_approval && ['approved', 'published', 'live'].includes(w.status)
        ).length;
        
        if (onPendingCountChange) {
          onPendingCountChange(pendingCount + modifiedCount);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load workshops');
        toast.error('Failed to load workshops');
      } finally {
        setLoading(false);
      }
    };
    
    fetchWorkshops();
  }, [onPendingCountChange]);

  // The platform's default share, so a workshop with no override still shows a real number
  useEffect(() => {
    axios
      .get(`${API}/admin/platform-fees`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => setDefaultFeePct(r.data.default_percent))
      .catch(() => {});
  }, [feeVersion]);
  
  // View full workshop details
  const handleView = async (workshop) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/workshops/admin/${workshop.id}/full`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSelectedWorkshop(response.data);
      setShowDetailModal(true);
    } catch (err) {
      toast.error('Failed to load workshop details');
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const handleApprove = async (workshop) => {
    try {
      await axios.put(
        `${API}/workshops/admin/${workshop.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`"${workshop.title}" approved!`);
      setWorkshops(prev => prev.map(w => 
        w.id === workshop.id ? { ...w, status: 'approved' } : w
      ));
    } catch (err) {
      toast.error('Failed to approve workshop');
    }
  };
  
  const handleReject = (workshop) => {
    setWorkshopToReject(workshop);
    setShowRejectModal(true);
  };
  
  const confirmReject = async (reason) => {
    if (!workshopToReject) return;
    
    try {
      await axios.put(
        `${API}/workshops/admin/${workshopToReject.id}/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`"${workshopToReject.title}" rejected`);
      setShowRejectModal(false);
      setWorkshopToReject(null);
      setWorkshops(prev => prev.map(w => 
        w.id === workshopToReject.id ? { ...w, status: 'rejected' } : w
      ));
    } catch (err) {
      toast.error('Failed to reject workshop');
    }
  };
  
  const handleDelete = async (workshop) => {
    try {
      await axios.delete(`${API}/workshops/admin/${workshop.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`"${workshop.title}" deleted`);
      setWorkshops(prev => prev.filter(w => w.id !== workshop.id));
      setShowDetailModal(false);
      setSelectedWorkshop(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete workshop');
    }
  };
  
  const handleDeactivate = async (workshop) => {
    try {
      await axios.put(
        `${API}/workshops/${workshop.id}/deactivate`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`"${workshop.title}" deactivated - existing purchasers retain access`);
      setWorkshops(prev => prev.map(w => 
        w.id === workshop.id ? { ...w, status: 'inactive' } : w
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate workshop');
    }
  };
  
  const handleReactivate = async (workshop) => {
    try {
      await axios.put(
        `${API}/workshops/${workshop.id}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`"${workshop.title}" reactivated and available for sale`);
      setWorkshops(prev => prev.map(w => 
        w.id === workshop.id ? { ...w, status: 'approved' } : w
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate workshop');
    }
  };
  
  const handleAcknowledgeModification = async (workshop) => {
    try {
      await axios.put(
        `${API}/workshops/admin/${workshop.id}/clear-modification`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Changes to "${workshop.title}" acknowledged`);
      setWorkshops(prev => prev.map(w => 
        w.id === workshop.id ? { ...w, modified_since_approval: false } : w
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to acknowledge modifications');
    }
  };
  
  // Filter
  const filteredWorkshops = workshops.filter(w => {
    const matchesSearch = !searchQuery || 
      w.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.creator_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const isPending = w.status === 'pending_review' || w.status === 'pending_admin_approval';
    const isApproved = w.status === 'approved' || w.status === 'published' || w.status === 'live';
    const isModified = w.modified_since_approval && isApproved;
    
    const matchesTab = 
      (activeTab === 'pending' && isPending) ||
      (activeTab === 'modified' && isModified) ||
      (activeTab === 'approved' && isApproved) ||
      (activeTab === 'rejected' && w.status === 'rejected') ||
      (activeTab === 'inactive' && w.status === 'inactive') ||
      (activeTab === 'draft' && w.status === 'draft') ||
      (activeTab === 'all');
    
    return matchesSearch && matchesTab;
  });
  
  // Counts
  const modifiedCount = workshops.filter(w => 
    w.modified_since_approval && ['approved', 'published', 'live'].includes(w.status)
  ).length;
  
  const counts = {
    pending: workshops.filter(w => w.status === 'pending_review' || w.status === 'pending_admin_approval').length,
    modified: modifiedCount,
    approved: workshops.filter(w => w.status === 'approved' || w.status === 'published' || w.status === 'live').length,
    rejected: workshops.filter(w => w.status === 'rejected').length,
    inactive: workshops.filter(w => w.status === 'inactive').length,
    draft: workshops.filter(w => w.status === 'draft').length,
    all: workshops.length
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mb-4" />
        <p className="text-gray-400">Loading workshops...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Workshop Management</h2>
          <p className="text-gray-400">Review, approve, and manage practitioner workshops</p>
        </div>
        
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            placeholder="Search workshops..."
            className="pl-10"
          />
        </div>
      </div>

      <PlatformFeeDefaultCard key={feeVersion} onChanged={() => setFeeVersion(v => v + 1)} />
      
      {/* Status Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); }}>
        <TabsList className="flex flex-wrap h-auto gap-1 p-1 justify-start">
          <TabsTrigger value="pending" className="relative flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 py-1.5">
            Pending
            {counts.pending > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">
                {counts.pending}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="modified" className="relative flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 py-1.5">
            Modified
            {counts.modified > 0 && (
              <span className="ml-1 bg-amber-500 text-white text-xs rounded-full px-1.5">
                {counts.modified}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 py-1.5">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="inactive" className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 py-1.5">Inactive ({counts.inactive})</TabsTrigger>
          <TabsTrigger value="rejected" className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 py-1.5">Rejected ({counts.rejected})</TabsTrigger>
          <TabsTrigger value="draft" className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 py-1.5">Drafts ({counts.draft})</TabsTrigger>
          <TabsTrigger value="all" className="flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 py-1.5">All ({counts.all})</TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-6">
          {filteredWorkshops.length === 0 ? (
            <Card className="bg-slate-800">
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-300">
                  {activeTab === 'pending' 
                    ? 'No workshops pending review'
                    : activeTab === 'modified'
                    ? 'No modified workshops requiring review'
                    : `No ${activeTab} workshops found`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div>
              {filteredWorkshops.slice((page - 1) * PER_PAGE, page * PER_PAGE).map(workshop => (
                <WorkshopCard
                  key={workshop.id}
                  workshop={workshop}
                  onView={handleView}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onDelete={handleDelete}
                  onDeactivate={handleDeactivate}
                  onReactivate={handleReactivate}
                  onAcknowledgeModification={handleAcknowledgeModification}
                />
              ))}
              <Pagination
                page={page}
                totalPages={Math.max(1, Math.ceil(filteredWorkshops.length / PER_PAGE))}
                onChange={setPage}
                testidPrefix="admin-workshops-pagination"
                theme="dark"
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Full Detail Modal */}
      <WorkshopDetailModal
        workshop={selectedWorkshop}
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedWorkshop(null);
        }}
        onDelete={handleDelete}
        onAcknowledge={handleAcknowledgeModification}
        loading={loadingDetails}
        defaultFeePct={defaultFeePct}
        onFeeSaved={(data) => {
          setSelectedWorkshop(w => (w ? { ...w, ...data } : w));
          setWorkshops(list => list.map(w => (w.id === data.id ? { ...w, ...data } : w)));
          setFeeVersion(v => v + 1);
        }}
      />
      
      {/* Reject Modal */}
      <RejectModal
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setWorkshopToReject(null);
        }}
        onConfirm={confirmReject}
        workshopTitle={workshopToReject?.title}
      />
    </div>
  );
};

export default AdminWorkshopsTab;
